import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLists } from '../context/ListsContext';
import { useLoginModal } from '../context/LoginModalContext';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import CreateListModal from '../components/CreateListModal/CreateListModal';
import './ListsManage.css';

const ListsManage = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user } = useAuth();
  const { lists, loaded, deleteList, renameList } = useLists();
  const { openLoginModal } = useLoginModal();
  const [deleting, setDeleting] = useState(null); // listId
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingListId, setEditingListId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const startRename = (list) => {
    if (list.is_favourite) return;
    setEditingListId(list.id);
    setEditingName(list.name);
  };

  const handleRenameSubmit = async (list) => {
    const trimmed = editingName.trim();
    if (!trimmed || trimmed === list.name) {
      setEditingListId(null);
      return;
    }
    setSavingRename(true);
    try {
      await renameList(list.id, trimmed);
      setEditingListId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename list');
    } finally {
      setSavingRename(false);
    }
  };

  const handleDeleteList = useCallback(async (list) => {
    if (!window.confirm(`Delete list "${list.name}"? This cannot be undone.`)) return;
    setDeleting(list.id);
    try {
      await deleteList(list.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete list');
    } finally {
      setDeleting(null);
    }
  }, [deleteList]);

  if (!user) {
    return (
      <div className="lists-manage-page">
        <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <p className="lists-manage-empty">Sign in to manage your lists.</p>
        <button 
          className="create-post-btn login-prompt-btn"
          onClick={openLoginModal}
        >
          <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
            <polyline points="10 17 15 12 10 7"></polyline>
            <line x1="15" y1="12" x2="3" y2="12"></line>
          </svg>
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="lists-manage-page">
      <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />

      <div className="lists-manage-header">
        <h1 className="lists-manage-title">Your Lists</h1>
        <button 
          className="create-list-page-btn header-create-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create new list
        </button>
      </div>

      {!loaded ? (
        <LoadingSpinner />
      ) : lists.length === 0 ? (
        <p className="lists-manage-empty">No lists yet. Add authors to a list from a post's ⋯ menu.</p>
      ) : (
        <ul className="lists-manage-list">
          {lists.map(list => (
            <li key={list.id} className="lists-manage-item">
              <div className="lists-manage-row">
                <div className="lists-manage-info">
                  {editingListId === list.id ? (
                    <input
                      type="text"
                      className="lists-manage-rename-input"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSubmit(list);
                        if (e.key === 'Escape') setEditingListId(null);
                      }}
                      autoFocus
                      disabled={savingRename}
                      maxLength={100}
                    />
                  ) : (
                    <span className="lists-manage-name">{list.name}</span>
                  )}
                  <span className="lists-manage-count">{list.members.length} {list.members.length === 1 ? 'author' : 'authors'}</span>
                </div>

                <div className="lists-manage-actions">
                  {editingListId === list.id ? (
                    <>
                      <button
                        className="lists-action-btn"
                        onClick={() => handleRenameSubmit(list)}
                        disabled={savingRename || !editingName.trim()}
                      >
                        {savingRename ? '…' : 'Save'}
                      </button>
                      <button
                        className="lists-action-btn"
                        onClick={() => setEditingListId(null)}
                        disabled={savingRename}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                        <button
                          className="lists-action-btn"
                          onClick={() => startRename(list)}
                          disabled={deleting === list.id || list.is_favourite}
                        >
                          Rename
                        </button>
                      <Link
                        to={`/user/${user.username}/lists/${encodeURIComponent(list.name)}`}
                        className="lists-action-btn lists-view-btn"
                        title="View list"
                      >
                        View list
                      </Link>
                      <button
                        className="lists-action-btn lists-delete-btn"
                        onClick={() => handleDeleteList(list)}
                        disabled={deleting === list.id || list.is_favourite}
                        title={list.is_favourite ? "Cannot delete favourite list" : "Delete list"}
                      >
                        {deleting === list.id ? '…' : 'Delete'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
          
          {/* Bottom create button */}
          <div className="lists-manage-bottom-actions">
            <button 
              className="create-list-page-btn bottom-create-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create new list
            </button>
          </div>
        </ul>
      )}

      {showCreateModal && (
        <CreateListModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
};

export default ListsManage;

